(function (algolia) {
  const { instantsearch } = algolia.externals;
  const { algoliasearch } = algolia.externals;
  const { sortBy } = algolia.externals.widgets;
  const { clearRefinements } = algolia.externals.widgets;
  const { panel } = algolia.externals.widgets;
  const { hits } = algolia.externals.widgets;
  const { pagination } = algolia.externals.widgets;
  const { connectCurrentRefinements } = algolia.externals.connectors;
  const { searchBox } = algolia.externals.widgets;
  const { queryRuleCustomData } = algolia.externals.widgets;
  let redirectCheck = 0;

  const collectionPageEnabled = algolia.is_collection_results_page
    && algolia.config.instant_search_enabled_on_collection;

  if (!algolia.full_results && !algolia.is_collection_results_page) {
    /**
     * If we aren't on the search page and neither on a collection page,
     * then there's no need to proceed
     */
    return;
  } if (algolia.full_results) {
    /**
     * If we are on the search page and instant search isn't enabled,
     * then there's no need to proceed
     */
    if (!algolia.config.instant_search_enabled) {
      return;
    }
  } else if (algolia.is_collection_results_page) {
    /**
     * If we are on a collection page and instant search on collection isn't
     * enabled, then there's no need to proceed
     */
    if (!algolia.config.instant_search_enabled_on_collection) {
      return;
    }
  }

  /**
   * Array which will contain all filters to be applied while initiating the
   * search API call.
   */
  const searchFilters = [];

  let collectionFacetFilter = null;
  let collectionRulesContextValue = null;
  let collectionHandle = null;

  if (collectionPageEnabled) {
    const matches = window.location.pathname.match(/\/collections\/([^/]+)/i);
    collectionHandle = Boolean(matches) && matches.length === 2 ? matches[1] : null;

    if (algolia.config.collection_id_indexing) {
      collectionFacetFilter = algolia.current_collection_id
        ? `collection_ids:"${algolia.current_collection_id}"`
        : null;
    } else {
      collectionFacetFilter = `collections:"${collectionHandle}"`;
    }

    // Add the collection filter to the list of search filters
    searchFilters.push(collectionFacetFilter);

    collectionRulesContextValue = algolia.config.collection_id_query_rules
      ? algolia.current_collection_id
      : collectionHandle;
  }

  // Filters for stock policy
  let stockPolicyFilter = null;
  if (algolia.config.stock_policy) {
    if (algolia.config.stock_policy === 'allow') {
      /**
       * For 'allow', we don't need to add any filter as we want to continue
       * displaying all out of stock items.
       */
    } else if (algolia.config.stock_policy === 'deny') {
      // For 'deny' we will filter out all items based on inventory quantity
      stockPolicyFilter = 'inventory_quantity > 0';
    } else if (algolia.config.stock_policy === 'continue') {
      /**
       * For 'continue' we will filter on `inventory_available` attribute whose
       * value is dependent on:
       * `inventory_quantity > 0 OR inventory_policy == 'continue'`
       */
      stockPolicyFilter = 'inventory_available:true';
    }

    // Add the stock policy filter to the list of search filters
    if (stockPolicyFilter) {
      searchFilters.push(stockPolicyFilter);
    }
  }

  // Extract ISO code from URL
  function extractIsoCodeFromUrl() {
    const match = window.location.pathname.match(/\/en-(\w\w)(\/|$)/);
    return match ? match[1] : null;
  }

  // Construct filter string based on ISO code
  function constructFilterForIsoCode(isoCode) {
    const capitalizedIsoCode = isoCode ? isoCode.toUpperCase() : null;
    return capitalizedIsoCode ? `NOT tags:do_not_sell_to_${capitalizedIsoCode}` : null;
  }

  // Add tag filter for hiding products based on user country
  const isoCode = extractIsoCodeFromUrl();
  const isoCodeFilter = constructFilterForIsoCode(isoCode);
  if (isoCodeFilter) {
    searchFilters.push(isoCodeFilter);
  }

  let results_selector = collectionPageEnabled
    ? algolia.config.collection_css_selector
    : algolia.config.results_selector;

  const activeSortOrders = collectionPageEnabled && algolia.collectionSortOrders
    ? algolia.collectionSortOrders
    : algolia.sortOrders;

  results_selector += ', .algolia-shopify-instantsearch';

  function getTrackedUiState(uiState) {
    const trackedUiState = {};
    Object.keys(uiState).forEach((k) => {
      if (k === 'configure' || k === 'query' || k === 'q') {
        return;
      }
      trackedUiState[k] = uiState[k];
    });
    return trackedUiState;
  }

  /**
   * List of URL params which are "allowed" by InstantSearch.
   * These parameters won't be overwritten when the search state is written
   * to the URL.
   */
  const ALLOWED_FOREIGN_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
  ];
  let foreignRouteState;

  function singleIndex(indexName) {
    return {
      /**
       * `stateToRoute` method transforms the `uiState` object from InstantSearch
       * into an object that will then be serialized to construct the URL.
       */
      stateToRoute(uiState) {
        const route = getTrackedUiState(uiState[indexName] || {});
        route.q = uiState[indexName].query;
        return { ...foreignRouteState, ...route };
      },

      /**
       * `routeToState` method transforms the route object into a `uiState` object
       * so that InstantSearch is initialized with the correct state from the
       * URL.
       */
      routeToState(routeState) {
        /**
         * Backup parameters from `routeState` into a different object
         * `foreignRouteState` so that we can re-inject them into the object
         * returned within `stateToRoute`.
         * This allows us to preserve the parameters specified in `ALLOWED_FOREIGN_PARAMS`
         * array.
         */
        if (!foreignRouteState) {
          foreignRouteState = ALLOWED_FOREIGN_PARAMS.reduce((acc, key) => {
            acc[key] = routeState[key];
            return acc;
          }, {});
        }

        const state = {};
        state[indexName] = getTrackedUiState(routeState || {});
        state[indexName].query = routeState.q;
        return state;
      },
    };
  }

  const instant = {
    colors: algolia.config.colors,
    distinct: Boolean(algolia.config.show_products),
    facets: {
      hidden:
        collectionPageEnabled && algolia.collectionHiddenFacets
          ? algolia.collectionHiddenFacets
          : algolia.hiddenFacets,
      shown:
        collectionPageEnabled && algolia.collectionShownFacets
          ? algolia.collectionShownFacets
          : algolia.shownFacets,
      list:
        collectionPageEnabled && algolia.collectionFacets
          ? algolia.collectionFacets
          : algolia.facets,
      widgets:
        collectionPageEnabled && algolia.collectionFacetsWidgets
          ? algolia.collectionFacetsWidgets
          : algolia.facetsWidgets,
    },
    hitsPerPage:
      collectionPageEnabled
      && algolia.config.collections_full_results_hits_per_page
        ? algolia.config.collections_full_results_hits_per_page
        : algolia.config.products_full_results_hits_per_page,
    search: instantsearch({
      searchClient: algoliasearch(
        algolia.config.app_id,
        algolia.config.search_api_key,
      ),
      insights: algolia.config.analytics_enabled,
      indexName: `${algolia.config.index_prefix}products`,
      routing: {
        stateMapping: singleIndex(`${algolia.config.index_prefix}products`),
      },
      searchFunction(searchFunctionHelper) {
        // Set query parameters here because they're not kept when someone
        // presses the Back button if set in the `init` function of a custom widget
        window.SDG.index = searchFunctionHelper.state.index;
        window.SDG.algoliaState = searchFunctionHelper.state;
        const page = searchFunctionHelper.getPage();
        if (instant.distinct) {
          searchFunctionHelper.setQueryParameter('distinct', true);
        }

        // Assign any required filters
        if (searchFilters.length) {
          searchFunctionHelper.setQueryParameter(
            'filters',
            searchFilters.join(' AND '),
          );
        }

        // Assign any required `ruleContexts` which are required for query rules
        // targeting collection pages
        const ruleContexts = [];
        // Add country context for hiding products based on user country
        ruleContexts.push(`country_${window.countryCode}`);

        if (collectionPageEnabled) {
          // Collection page merchandising:
          // If we are on a collection page, `collectionRulesContextValue` is defined
          if (collectionRulesContextValue) {
            ruleContexts.push(collectionRulesContextValue.toString());
          }

          // get utm params from session storage
          const utmParam = sessionStorage.getItem('utm_medium');

          // add utm params to rule contexts
          if (utmParam) {
            ruleContexts.push(`utm_campaign-${utmParam}`);
          }
        }
        searchFunctionHelper.setQueryParameter('ruleContexts', ruleContexts);

        searchFunctionHelper.setPage(page);
        searchFunctionHelper.search();
      },
    }),
    selector: `${results_selector}, .algolia-shopify-instantsearch`,
    sortOrders: activeSortOrders,
  };
  instant.search.client.addAlgoliaAgent('Shopify Integration');
  algolia.instantsearch = instant;

  function readjust() {
    const width = instant.$results.offsetWidth;

    let suffix = 'lg';
    if (width < 400) suffix = 'xs';
    else if (width < 800) suffix = 'sm';
    else if (width < 1200) suffix = 'md';

    instant.$results.classList.remove('ais-results-size-xs');
    instant.$results.classList.remove('ais-results-size-sm');
    instant.$results.classList.remove('ais-results-size-md');
    instant.$results.classList.remove('ais-results-size-lg');
    instant.$results.classList.add(`ais-results-size-${suffix}`);
  }

  // Link CSS and set up CSS variables
  const cssFile = document.getElementById('template_algolia_instant_search.css');
  document.documentElement.style.setProperty('--main-color', `${algolia.config.colors.main}`);
  document.documentElement.style.setProperty('--secondary-color', `${algolia.config.colors.secondary}`);
  document.documentElement.style.setProperty('--highlight-bg-color', `${algolia.helpers.hexToRGB(algolia.config.colors.highlight)}`);
  algolia.appendStyle(cssFile.text);

  if (collectionPageEnabled) {
    if (
      document.querySelectorAll(algolia.config.collection_css_selector)
        .length === 0
    ) {
      throw new Error(
        'Instant search CSS selector for collection page is incorrect\nFor more info see: https://www.algolia.com/doc/integration/shopify/advanced-customization/collection-search-page/#css-selector',
      );
    }
  } else if (
    document.querySelectorAll(algolia.config.results_selector).length === 0
  ) {
    throw new Error(
      'Instant search CSS selector is incorrect\nFor more info see: https://www.algolia.com/doc/integration/shopify/building-search-ui/instant-search/#css-selector',
    );
  }

  // Instantiating the main page
  instant.$results = document.querySelector(instant.selector);
  instant.$results.innerHTML = '';

  algolia.render(algolia.instantSearchMainTemplate, instant.$results, {
    facets: instant.facets.list,
    multipleSortOrders: activeSortOrders.length > 1,
  });

  readjust();

  window.addEventListener('resize', () => {
    readjust();
  });

  // Custom actions and event listeners
  instant.search.addWidgets([
    {
      init() {
        // show hide the facets drawer
        const $buttons = document.querySelectorAll('.ais-facets-button');
        const $mainContent = document.getElementById('MainContent');
        const $facets = document.querySelector('.ais-facets');

        /**
         * Close on outside click
         */
        const closeOnOutsideClick = (event) => {
          if (event.target.id === 'MainContent') {
            const $closeButton = $facets.querySelector('.ais-facets-button');
            $closeButton.click();
          }
        };

        /**
         * Show facets drawer
         */
        const showFacetsDrawer = () => {
          document.addEventListener('click', closeOnOutsideClick);
          const $closeDrawerBtn = document.querySelector('.js-close-facets');
          const $collapsiblePanels = [...$facets.querySelectorAll('.ais-Panel--collapsible:not(.ais-Panel--noRefinement, .ais-Panel--collapsed)')];
          // Show facets drawer
          $facets.classList.add('ais-facets__shown');
          // Show overlay
          $mainContent.classList.add('ais-facets-shown-overlay');
          // Close all except the first two panels
          $collapsiblePanels.forEach(($panel, index) => {
            if (index < 2) return;
            const $collapseButtons = [...$panel.querySelectorAll('.ais-Panel-collapseButton')];
            $collapseButtons.forEach(($collapseButton) => $collapseButton.click());
          });

          // Trap focus within the drawer
          const focusableElements = $facets.querySelectorAll('a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])');
          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          const trapFocus = (event) => {
            if (event.key === 'Tab') {
              if (event.shiftKey) { // Shift + Tab
                if (document.activeElement === firstFocusableElement) {
                  event.preventDefault();
                  lastFocusableElement.focus();
                }
              } else { // Tab
                // eslint-disable-next-line no-lonely-if
                if (document.activeElement === lastFocusableElement) {
                  event.preventDefault();
                  firstFocusableElement.focus();
                }
              }
            }
          };

          $facets.addEventListener('keydown', trapFocus);

          if ($closeDrawerBtn) {
            setTimeout(() => {
              $closeDrawerBtn.focus();
            }, 100);
          }
        };

        /**
         * Hide facets drawer
         */
        const hideFacetsDrawer = () => {
          // Hide facets drawer
          $facets.classList.remove('ais-facets__shown');
          // Remove overlay
          $mainContent.classList.remove('ais-facets-shown-overlay');
          document.removeEventListener('click', closeOnOutsideClick);
          const $filtersBtn = document.querySelector('.facets-toolbar--left .ais-facets-button');

          if ($filtersBtn) {
            setTimeout(() => {
              $filtersBtn.focus();
            }, 10);
          }
        };

        [...$buttons].forEach(($button) => {
          $button.addEventListener('click', () => {
            if ($facets.classList.contains('ais-facets__shown')) {
              hideFacetsDrawer();
            } else {
              showFacetsDrawer();
            }
          });
        });
      },

    },
  ]);

  // Logo & clear
  instant.search.addWidgets([
    {
      init(opts) {
        document
          .querySelector('.ais-clear-input-icon')
          .addEventListener('click', () => {
            opts.helper.setQuery('').search();
            const input = document.querySelector('.ais-search-box--input');
            input.value = '';
            input.focus();
          });
      },
      render(opts) {
        if (!opts.state.query) {
          document.querySelector('.ais-clear-input-icon').style.display = 'none';
        } else {
          document.querySelector('.ais-clear-input-icon').style.display = '';
        }
      },
    },
  ]);

  // Sort orders
  if (activeSortOrders.length > 1) {
    instant.search.addWidgets([
      sortBy({
        container: '.ais-sort-orders-container',
        items: instant.sortOrders,
      }),
    ]);
  }

  // Hidden facets
  const list = instant.facets.hidden.map((facet) => facet.name);
  instant.search.addWidgets([
    {
      getConfiguration() {
        return {
          facets: list,
          disjunctiveFacets: list,
        };
      },
      init() {},
    },
  ]);

  // Create the render function
  const createDataAttributes = function (refinement) {
    return Object.keys(refinement)
      .map((key) => `data-${key}="${encodeURIComponent(refinement[key] || '')}"`)
      .join(' ');
  };

  const renderListItem = function (item) {
    return item.refinements
      .map((refinement) => {
        const randomString = Math.random().toString(36).substring(2, 15);
        return (
          '<li class="ais-current-refined-values--item">'
            + `  <button type="button" ${
              createDataAttributes(refinement)
            } class="ais-current-refined-values--link ${refinement.attribute !== 'options.color' ? 'text-label' : null}">`
            + `${refinement.attribute === 'options.color' ? algolia.helpers.setColorSwatchRefinement(refinement) : refinement.label}`
            + `<svg xmlns="http://www.w3.org/2000/svg" aria-labelledby="${randomString}" width="8" height="8" viewBox="0 0 8 8" fill="none" role="img">
            <title id="${randomString}">Clear</title>
            <path d="M4.70717 4.00006L8 7.29289L7.29289 8L4.00006 4.70717L0.707229 8.00012L0.00012207 7.29301L3.29296 4.00006L0 0.707106L0.707107 0L4.00006 3.29295L7.29289 0L8 0.707106L4.70717 4.00006Z" fill="#051C2C"/>
            </svg></button>`
            + '</li>'
        );
      })
      .join('');
  };

  const renderCurrentRefinements = function (renderOptions) {
    const { items } = renderOptions;
    const { refine } = renderOptions;
    const { widgetParams } = renderOptions;

    widgetParams.container.innerHTML = '<div class="ais-root ais-current-refined-values ais-facet">'
      + `  <ul class="ais-current-refined-values--list">${
        items.map(renderListItem).join('')
      }  </ul>`
      + '</div>';

    Array.prototype.slice
      .call(
        widgetParams.container.querySelectorAll(
          '.ais-current-refined-values--link',
        ),
      )
      .forEach((element) => {
        element.addEventListener('click', (event) => {
          const item = Object.keys(event.currentTarget.dataset).reduce(
            (
              acc,
              key,
            ) => {
              const itemData = {};
              itemData[key] = decodeURIComponent(event.currentTarget.dataset[key]);
              return algolia.assign({}, acc, itemData);
            },
            {},
          );
          refine(item);
        });
      });
  };

  // Create the custom widget
  const customCurrentRefinements = connectCurrentRefinements(
    renderCurrentRefinements,
  );

  const hasRefinements = function (helper, facetsList) {
    return facetsList.some((f) => {
      const isConjunctiveFacetRefined = helper.state.isConjunctiveFacet(f.name)
        && helper.state.isFacetRefined(f.name);
      const isDisjunctiveFacetRefined = helper.state.isDisjunctiveFacet(f.name)
        && helper.state.isDisjunctiveFacetRefined(f.name);
      const isHierarchicalFacetRefined = helper.state.isHierarchicalFacet(f.name)
        && helper.state.isHierarchicalFacetRefined(f.name);

      const numericRefinements = helper.state.getNumericRefinements(f.name);
      const isNumericFacetRefined = Object.keys(numericRefinements).some((
        operator,
      ) => numericRefinements[operator].length > 0);

      return (
        isNumericFacetRefined
        || isConjunctiveFacetRefined
        || isDisjunctiveFacetRefined
        || isHierarchicalFacetRefined
      );
    });
  };

  /**
   * Decides whether the given `facetName` has any facet values available in
   * the current result set by checking the available `facets`,
   * `disjunctiveFacets` and `hierarchicalFacets`.
   */
  const hasFacetValues = function (resultState, facetName) {
    const isFacetValueAvailable = resultState.facets.some((facet) => facet.name === facetName);
    const isDisjunctiveFacetValueAvailable = resultState.disjunctiveFacets.some(
      (facet) => facet.name === facetName,
    );
    const isHierarchicalFacetValueAvailable = resultState.hierarchicalFacets.some(
      (facet) => facet.name === facetName,
    );

    return (
      isFacetValueAvailable
      || isDisjunctiveFacetValueAvailable
      || isHierarchicalFacetValueAvailable
    );
  };

  const customCurrentRefinementsWithPanel = panel({
    hidden(options) {
      return !hasRefinements(options.helper, instant.facets.list);
    },
  })(customCurrentRefinements);

  const clearRefinementsWithPanel = panel({
    hidden(options) {
      return !hasRefinements(options.helper, instant.facets.list);
    },
  })(clearRefinements);

  const customCurrentRefinementsWithPanelToolbar = panel({
    hidden(options) {
      return !hasRefinements(options.helper, instant.facets.list);
    },
  })(customCurrentRefinements);

  const clearRefinementsWithPanelToolbar = panel({
    hidden(options) {
      return !hasRefinements(options.helper, instant.facets.list);
    },
  })(clearRefinements);

  instant.search.addWidgets([
    clearRefinementsWithPanel({
      container: document.querySelector('.ais-clear-refinements-container'),
      templates: {
        resetLabel: algolia.translations.clearAll,
      },
    }),
    customCurrentRefinementsWithPanel({
      container: document.querySelector(
        '.ais-current-refined-values-container',
      ),
    }),
  ]);

  instant.search.addWidgets([
    clearRefinementsWithPanelToolbar({
      container: document.querySelector('.ais-current-refined-values-container--toolbar'),
      templates: {
        resetLabel: algolia.translations.clearAll,
      },
    }),
    customCurrentRefinementsWithPanelToolbar({
      container: document.querySelector(
        '.ais-current-refined-values-container--toolbar',
      ),
    }),
  ]);
  /**
   * Setup the facets for search.
   * We are using a `panel` with a custom `hidden` function which will allow
   * us to hide the parent container of the facet if there are no facet values
   * available for that particular facet.
   */

  instant.facets.widgets.forEach((widget) => {
    /**
     * Format heading text
     * https://www.algolia.com/doc/integration/shopify/sending-and-managing-data/named-tags
     */
    let headerText = widget.params.attribute.toLowerCase();
    if (headerText.startsWith('options.')) {
      headerText = headerText.replace('options.', '');
    }
    if (headerText.startsWith('named_tags.')) {
      headerText = headerText.replace('named_tags.', '');
    }
    headerText = headerText.replaceAll(/_/g, ' ');

    const facetWithPanel = panel({

      cssClasses: {
      },
      templates: {
        header: headerText,
        body: 'panel-body',
      },
      collapsed() {
        const collapseSVG = document.querySelectorAll('.ais-Panel-collapseIcon');
        if (collapseSVG.length > 0) {
          [...collapseSVG].forEach((svg) => {
            svg.parentNode.classList.add('ais-Panel-collapseIcon--wrapper');
          });
        }
        return false;
      },

      hidden(options) {
        const hide = !hasFacetValues(options.results, widget.params.attribute);
        const displayStyle = hide ? 'none' : '';

        /**
         * Replacing any "." and "%" in the class name with "\." and "\%" so that querySelector
         * can work as expected. "." is supposed to be present for named tags
         * and metafield based facet names and "%" is present when the facet name has a space
         * in it because of the encoding.
         */
        const parentContainerClassName = `.ais-facet-${encodeURIComponent(
          widget.params.attribute,
        ).replace(/\./g, '\\.').replace(/\%/g, '\\%')}`;
        const customClassName = `ais-facet-${widget.params.attribute.replace('.', '-')}`;
        const parentContainer = document.querySelector(parentContainerClassName);
        parentContainer.classList.add(customClassName);
        parentContainer.setAttribute('data-facet-name', widget.params.attribute);
        if (parentContainer) {
          parentContainer.style.display = displayStyle;
        }

        return hide;
      },
    })(widget.widget);
    instant.search.addWidgets([facetWithPanel(widget.params)]);
  });

  // Hits
  instant.search.addWidgets([
    hits({
      container: '.ais-hits-container',
      templates: {
        item(hit, { html, components }) {
          return algolia.instantSearchProductTemplate(hit, html, components);
        },
        empty(results, { html }) {
          return algolia.instantSearchNoResultTemplate(html);
        },
      },
      transformItems(items) {
        localStorage.setItem('queryID', instant.search.helper.lastResults.queryID);

        return items.map((item) => ({
          ...item,
          _distinct: instant.distinct,
          can_order:
            item.inventory_policy === 'continue' || item.inventory_quantity > 0,
          queryID: item.__queryID,
          productPosition: item.__position,
          index: instant.search.mainIndex.getIndexName(),
        }));
      },
    }),
  ]);

  // No result actions
  instant.search.addWidgets([
    {
      init(opts) {
        const container = document.querySelector('.ais-hits-container');
        container.addEventListener('click', (e) => {
          let el = e.target;
          let clearFilters;
          do {
            if (el.matches('.ais-hit-empty--clear-filters')) {
              clearFilters = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!clearFilters) {
            return;
          }

          const { helper } = opts;
          helper.clearTags();
          instant.facets.list.forEach((facet) => {
            helper.clearRefinements(facet.name);
          });
          helper.search();
        });

        container.addEventListener('click', (e) => {
          let el = e.target;
          let clearInput;
          do {
            if (el.matches('.ais-hit-empty--clear-input')) {
              clearInput = el;
              break;
            }
            el = el.parentNode;
          } while (el && el !== container);
          if (!clearInput) {
            return;
          }

          opts.helper.setQuery('').search();
          const input = document.querySelector('.ais-search-box--input');
          input.value = '';
          input.focus();
        });

        const skeleton = document.querySelector('.js-plp-skeleton');
        if (skeleton) {
          skeleton.remove();
        }
      },
    },
  ]);

  // Pagination
  instant.search.addWidgets([
    pagination({
      container: '.ais-pagination-container--desktop',
      padding: 3,
      showFirst: false,
      showLast: false,
      templates: {
        next: '',
        previous: '',
      },
    }),
  ]);

  // Pagination
  instant.search.addWidgets([
    pagination({
      container: '.ais-pagination-container--mobile',
      padding: 2,
      showFirst: false,
      showLast: false,
      templates: {
        next: '',
        previous: '',
      },
    }),
  ]);

  // Search input
  instant.search.addWidgets([
    searchBox({
      container: '.ais-search-box-container',
      placeholder: algolia.translations.searchForProduct,
      showReset: false,
      showSubmit: false,
    }),
  ]);

  instant.search.addWidgets([
    queryRuleCustomData({
      container: '.ais-search-box-container',
      templates: {
        default: '',
      },
      transformItems(items) {
        const match = items.find((data) => Boolean(data.redirect));
        let isRedirect = false;

        if (match && match.redirect) {
          try {
            const redirectUrl = new URL(match.redirect);
            const currentUrl = new URL(window.location.href);
            isRedirect = true;

            if (currentUrl.hostname === redirectUrl.hostname) {
              const isoMatch = currentUrl.pathname.match(/\/en-(\w\w)(\/|$)/);
              if (isoMatch) {
                const isoCode2 = `/en-${isoMatch[1]}`;
                redirectUrl.pathname = isoCode2 + redirectUrl.pathname;
              }
            }

            window.location.href = redirectUrl.href;
          } catch (e) {
            console.error('Invalid redirect URL:', match.redirect, error);
          }
        }
        if (redirectCheck > 1 && !isRedirect && algolia.contentHide.parentNode) {
          algolia.contentHide.parentNode.removeChild(algolia.contentHide);
        }
        // Fallback if the above case fails
        window.debounce(500, () => {
          if (algolia.contentHide.parentNode) {
            algolia.contentHide.parentNode.removeChild(algolia.contentHide);
          }
        });

        redirectCheck += 1;
        return [];
      },
    }),
  ]);

  // Render handler function
  function handleRender() {
    cleanUpEventListeners();

    // All items are rendered in the DOM, we can now style them
    const isSearchPage = window.location.pathname === '/search';
    const searchHeader = document.getElementById('searchHeader');
    const searchQuery = searchHeader.querySelector('.js-search-query');

    if (searchHeader && isSearchPage) {
      searchHeader.classList.remove('hide');
      searchQuery.innerHTML = SDG.algoliaState.query;
    }

    try {
      // always fetch product swatches when render is called
      window.algoliaColorSwatchesInit();

      // insert in grid touts when render is called
      SDG.plpInGridTouts().init();

      // rerun quick add when render is called
      SDG.plpQuickAdd();
      SDG.collectionToolbar();
      setTimeout(() => {
        SDG.currencyFormatter().init();
      }, 1700);
    } catch (e) {
      console.error('Error initializing algolia search post-render functionality:', e);
    }

    const $clearRefinementsBtn = document.querySelector('.ais-ClearRefinements-button');
    if ($clearRefinementsBtn) {
      $clearRefinementsBtn.classList.add('button', 'button--secondary');
    }
  }

  function cleanUpEventListeners() {
    const allEvents = window.SDG.eventListenersToRemoveBeforePLPUpdate;
    if (allEvents) {
      allEvents.forEach((event) => {
        event();
      });
      window.SDG.eventListenersToRemoveBeforePLPUpdate = [];
    }
  }

  // Attach debounced render handler
  instant.search.on('render', window.debounce(handleRender, 500));

  instant.search.start();

  algolia.contentHide.parentNode.removeChild(algolia.contentHide);
}(window.algoliaShopify));
